import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const token = sessionStorage.getItem('token');
    const savedUser = sessionStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Refresh user from server so permission changes (e.g. canViewACC) reflect
        // without requiring a logout/login cycle.
        axios.get(`${API_BASE_URL}/auth/me`)
          .then(res => {
            if (res.data) {
              sessionStorage.setItem('user', JSON.stringify(res.data));
              setUser(res.data);
            }
          })
          .catch(() => { /* leave cached user in place; 401 handler will clear it if needed */ });
      } catch (e) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
    }
    setLoading(false);

    // Listen for session expiry from the Axios 401 interceptor
    const handleSessionExpired = () => {
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  /**
   * Step 1: Login with email/password
   * Returns { mfaRequired, userId, email } on success — does NOT issue token yet
   */
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      });

      const data = response.data;

      // MFA required — return the MFA challenge info
      if (data.mfaRequired) {
        return {
          success: true,
          mfaRequired: true,
          userId: data.userId,
          email: data.email,
        };
      }

      // Fallback: if backend ever returns token directly (shouldn't happen with MFA)
      if (data.token) {
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        return { success: true };
      }

      return { success: false, error: 'Unexpected response from server' };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please try again.'
      };
    }
  };

  /**
   * Step 2: Verify MFA code
   * On success, stores token and sets user — completing the login
   */
  const verifyMfa = async (userId, code) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-mfa`, {
        userId,
        code,
      });

      const { token, user: userData } = response.data;

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true };
    } catch (error) {
      console.error('MFA verification error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Verification failed. Please try again.'
      };
    }
  };

  /**
   * Resend MFA code
   */
  const resendMfa = async (userId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-mfa`, {
        userId,
      });

      return {
        success: true,
        email: response.data.email,
        userId: response.data.userId,
      };
    } catch (error) {
      console.error('Resend MFA error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend code. Please try again.'
      };
    }
  };

  const logout = () => {
    // Fire-and-forget server-side token invalidation
    const token = sessionStorage.getItem('token');
    if (token) {
      axios.post(`${API_BASE_URL}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Best-effort; clear local state regardless
    }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    login,
    verifyMfa,
    resendMfa,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
